import { getTodayString } from "@src/packages/utils/date";
import { cycleMutationInvalidationKeys } from "@src/packages/cycles/cycle-cache-keys";
import {
  useQuery,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import type { Patient } from "@louise/medical-history/basic-info/calculateBasicInfoCompletion";
import { retryLouiseQuery, useApiClient } from "@src/packages/api/louise-api";
import { throwApiError } from "@src/packages/api/api-error";
import { createIdempotencyKey } from "@src/packages/api/idempotency";
import type { Treatment } from "@louise/medical-history/basic-info/types";
import { RequestBody, ResponseBody } from "@src/packages/openapi/helpers";
import { paths } from "@src/packages/openapi/schema";
import {
  buildUpdatePatientPatchOptions,
  prepareUpdatePatientInput,
  type UpdatePatientInput,
} from "../update-patient-request";
import { mergePatientUpdate } from "../sync/mergePatientUpdate";
import type {
  LocalCycle,
  CycleType,
  TreatmentPhaseName,
  TreatmentStepProgress,
  TreatmentStepStatus,
} from "@louise/care/treatment-phases";

/**
 * Mapping from step IDs to the phase that should be activated when the step is completed.
 * When a step is marked as "completed" or "skipped", the cycle's currentPhase advances
 * to the next logical phase in the treatment flow.
 *
 * Step IDs match the API phase types from the Doctor Dashboard exactly.
 * Key: `${cycleType}:${stepId}` (e.g., "ivf:pre_treatment", "ivf:protocol")
 * Value: The TreatmentPhaseName to advance to
 */
const STEP_TO_NEXT_PHASE: Record<string, TreatmentPhaseName> = {
  // IVF: pre_treatment → protocol → egg_retrieval → fertilization → embryo_development → transfer → luteal_phase
  "ivf:pre_treatment": "stimulation",
  "ivf:protocol": "retrieval",
  "ivf:egg_retrieval": "fertilization",
  "ivf:embryo_development": "transfer",
  "ivf:transfer": "waiting",
  "ivf:luteal_phase": "complete",

  // FET: pre_treatment → protocol → transfer → luteal_phase
  "fet:pre_treatment": "baseline",
  "fet:protocol": "transfer",
  "fet:transfer": "waiting",
  "fet:luteal_phase": "complete",

  // IUI: pre_treatment → protocol → sperm_collection → insemination → luteal_phase
  "iui:pre_treatment": "stimulation",
  "iui:protocol": "transfer",
  "iui:insemination": "waiting",
  "iui:luteal_phase": "complete",

  // ICSI: pre_treatment → protocol → egg_retrieval → sperm_collection → fertilization → embryo_development → transfer → luteal_phase
  "icsi:pre_treatment": "stimulation",
  "icsi:protocol": "retrieval",
  "icsi:egg_retrieval": "fertilization",
  "icsi:embryo_development": "transfer",
  "icsi:transfer": "waiting",
  "icsi:luteal_phase": "complete",

  // OI: pre_treatment → protocol → luteal_phase
  "oi:pre_treatment": "stimulation",
  "oi:protocol": "waiting",
  "oi:luteal_phase": "complete",

  // Egg Freezing: pre_treatment → stimulation → trigger_shot → egg_retrieval → freezing
  "egg_freezing:pre_treatment": "stimulation",
  "egg_freezing:stimulation": "trigger",
  "egg_freezing:trigger_shot": "retrieval",
  "egg_freezing:egg_retrieval": "retrieval", // stays in retrieval — freezing is same day
  "egg_freezing:freezing": "complete",
  "egg_freezing:protocol": "retrieval", // legacy fallback

  // Embryo Freezing: pre_treatment → protocol → egg_retrieval → fertilization
  "embryo_freezing:pre_treatment": "stimulation",
  "embryo_freezing:protocol": "retrieval",
  "embryo_freezing:egg_retrieval": "fertilization",
  "embryo_freezing:fertilization": "complete",

  // Sperm Freezing: pre_treatment → screening → sperm_collection → analysis → freezing
  "sperm_freezing:pre_treatment": "baseline",
  "sperm_freezing:screening": "retrieval",
  "sperm_freezing:sperm_collection": "retrieval",
  "sperm_freezing:analysis": "retrieval",
  "sperm_freezing:freezing": "complete",
};

/**
 * Get the next phase name when a step is completed for a given cycle type.
 * Returns null if no phase advancement is defined for this step.
 */
function getNextPhaseForStep(
  cycleType: CycleType,
  stepId: string,
): TreatmentPhaseName | null {
  const key = `${cycleType}:${stepId}`;
  return STEP_TO_NEXT_PHASE[key] ?? null;
}

/**
 * Hook to get patient data with React Query
 */
export function usePatient(opts?: { patientId?: string | null }) {
  const { client, tenantId, patientId: currentPatientId } = useApiClient();
  // Undefined preserves the current-patient default; explicit null disables.
  const patientId =
    opts?.patientId === null ? null : (opts?.patientId ?? currentPatientId);

  return useQuery<Patient>({
    queryKey: ["patient", tenantId, patientId],
    queryFn: async () => {
      if (!patientId) {
        throw new Error("patientId is required");
      }
      // Deliberately unversioned: the medical-history flows read the legacy
      // patient shape, which every dated contract replaces with conditions.
      // Pin a version only with the conditions migration (#2331).
      const { data, error, response } = await client.GET(
        "/{tenantId}/patients/{patientId}",
        {
          params: {
            path: {
              tenantId,
              patientId,
            },
          },
        },
      );

      if (data) {
        return data.data;
      } else {
        throwApiError(
          error,
          "Failed to load patient",
          "usePatient",
          response.status,
        );
      }
    },
    enabled: !!patientId && !!tenantId,
    retry: retryLouiseQuery,
  });
}

/**
 * Hook to get patient data with React Query using Suspense
 */
export function useSuspensePatient() {
  const { client, tenantId, patientId } = useApiClient();

  return useSuspenseQuery<Patient>({
    queryKey: ["patient", tenantId, patientId],
    queryFn: async () => {
      // Suspense queries cannot be disabled with `enabled`, and openapi-fetch
      // sends the literal `{patientId}` placeholder when the id is undefined
      // — two junk 400s per Care load while identity resolves (#2651). Fail
      // locally instead; the key includes patientId, so the query re-runs
      // the moment identity arrives.
      if (!tenantId || !patientId) {
        throw new Error("patient identity is not resolved yet");
      }
      // Unversioned for the same reason as usePatient.
      const { data, error, response } = await client.GET(
        "/{tenantId}/patients/{patientId}",
        {
          params: {
            path: {
              tenantId,
              patientId,
            },
          },
        },
      );

      if (data) {
        return data.data;
      }
      throwApiError(
        error,
        "Failed to load patient",
        "useSuspensePatient",
        response?.status,
      );
    },
  });
}

export type { UpdatePatientInput } from "../update-patient-request";

export function useUpdatePatient() {
  const { client, tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = ["patient", tenantId, patientId];

  return useMutation({
    mutationFn: async (input: UpdatePatientInput) => {
      const { data, idempotencyKey, signal } = prepareUpdatePatientInput(input);
      if (!tenantId || !patientId) {
        if (data.phoneNumber !== undefined) {
          throw new Error(
            "Cannot save phone number before the patient is provisioned",
          );
        }
        console.warn(
          "[useUpdatePatient] Skipping PATCH: missing tenantId or patientId (user may not be fully provisioned)",
        );
        return data;
      }
      const {
        data: responseData,
        error,
        response,
      } = await client.PATCH(
        "/{tenantId}/patients/{patientId}",
        buildUpdatePatientPatchOptions({
          tenantId,
          patientId,
          data,
          idempotencyKey,
          signal,
        }),
      );
      if (error) {
        console.error("[useUpdatePatient] PATCH error:", error);
        throwApiError(
          error,
          "Failed to update patient",
          "useUpdatePatient",
          response?.status,
        );
      }
      return responseData;
    },
    onMutate: async (input: UpdatePatientInput) => {
      const { data } = prepareUpdatePatientInput(input);
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<Patient | undefined>(queryKey);

      queryClient.setQueryData<Patient | undefined>(
        queryKey,
        (current) =>
          mergePatientUpdate(
            current,
            data as Record<string, unknown>,
          ) as Patient,
      );

      return { previous };
    },
    onError: (error, _variables, context) => {
      // On an aborted mutation, do NOT restore the pre-mutation snapshot: a
      // newer concurrent toggle may have already applied its own optimistic
      // state, and blindly restoring `previous` would wipe it. Let the
      // onSettled invalidation reconcile the cache from the server instead.
      if (error?.name === "AbortError") return;
      // Same guard for genuine errors: only roll back to the snapshot when this
      // is the last in-flight patient mutation. If others are still pending,
      // restoring `previous` would clobber their optimistic state — leave the
      // cache as-is and let the final settle's invalidation reconcile it.
      if (queryClient.isMutating({ mutationKey: ["patient", patientId] }) > 1) {
        return;
      }
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    mutationKey: ["patient", patientId],
    onSettled: () => {
      // Only invalidate once the last in-flight patient mutation has settled,
      // so concurrent toggles don't refetch (and clobber each other's
      // optimistic state) mid-flight. This runs on every settle — including
      // aborts — so an aborted final mutation still reconciles the cache.
      if (
        queryClient.isMutating({ mutationKey: ["patient", patientId] }) <= 1
      ) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/**
 * Homepage stage type (matches backend enum)
 */
export type HomepageStage =
  | "explore"
  | "get-ready"
  | "before-treatment"
  | "in-care"
  | "time-to-wait"
  | "complete"
  // Donor-specific stages
  | "screening"
  | "matched"
  | "stimulation"
  | "retrieval"
  | "approved"
  | "donating";

/**
 * Hook to update patient's homepage stage
 */
// TODO: HomepageStage includes donor-specific values not in the API schema.
// This hook is a no-op until the API schema is updated or callers are fixed.
export function useUpdateHomepageStage() {
  return useMutation({
    mutationFn: (_stage: HomepageStage) => Promise.resolve(),
  });
}

/**
 * Hooks for Fertility Treatments CRUD (using Cycles API)
 */

export type CreateCycleBody = RequestBody<paths["/{tenantId}/cycles"]["post"]>;
export type CreateCycleInput = Omit<CreateCycleBody, "patientId">;
export type UpdateCycleBody = RequestBody<
  paths["/{tenantId}/cycles/{id}"]["patch"]
>;
type CycleResponse = ResponseBody<
  paths["/{tenantId}/patients/{patientId}/cycles"]["get"]
>;
export type Cycle = CycleResponse["data"][number];

/**
 * Cycle deletion — DELIBERATE product surface for exactly one flow (#2240):
 * removing an anamnesis (medical-history) past-treatment entry the patient
 * recorded by mistake. Active/completed treatment cycles are never deleted
 * from product flows — they are closed with an outcome. Do not add new
 * consumers without a product decision.
 */
export function useDeleteCycle() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (cycleId: string) => {
      console.log("[useDeleteCycle] DELETE request:", {
        endpoint: "/{tenantId}/cycles/{id}",
        tenantId,
        cycleId,
      });
      return client.DELETE("/{tenantId}/cycles/{id}", {
        headers: { "x-api-version": "2026-06-17" },
        params: {
          path: {
            tenantId,
            id: cycleId,
          },
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patient"],
      });
      for (const key of cycleMutationInvalidationKeys()) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}

export function useUpdateTreatment() {
  const { tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    // TODO: fertility-treatments endpoint not in API schema — no-op until schema updated
    mutationFn: (_args: { treatmentId: string; data: Treatment }) =>
      Promise.resolve(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patient", tenantId, patientId],
      });
    },
  });
}

export function useDeleteTreatment() {
  const { tenantId, patientId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    // TODO: fertility-treatments endpoint not in API schema — no-op until schema updated
    mutationFn: (_treatmentId: string) => Promise.resolve(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patient", tenantId, patientId],
      });
    },
  });
}

/**
 * Hook to update treatment step progress for a cycle
 *
 * Features optimistic UI updates: the UI will update immediately when you call
 * the mutation, then the actual data is persisted to storage. If the persist fails,
 * the UI will roll back to the previous state.
 */
type StepProgressUpsertBody = NonNullable<
  paths["/cycles/{cycleId}/step-progress/{stepId}"]["put"]["requestBody"]
>["content"]["application/json"];

export function useUpdateStepProgress() {
  const queryClient = useQueryClient();
  const { client, tenantId, patientId } = useApiClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      stepId,
      data,
    }: {
      cycleId: string;
      stepId: string;
      data: Partial<{
        status: "pending" | "completed" | "skipped" | "cancelled";
        completedAt: string | null;
        medications: {
          id: string;
          name: string;
          dosage: string | null;
          frequency: string | null;
          dateTaken: string | null;
          timeTaken: string | null;
        }[];
        data: Record<string, string | number | boolean | null>;
        notes: string | null;
      }>;
    }) => {
      // The PUT is a whole-row upsert, so merge the patch over the persisted
      // row first — sending the patch alone would reset omitted fields.
      const { data: existingData } = await client.GET(
        "/cycles/{cycleId}/step-progress",
        { params: { path: { cycleId } } },
      );
      const existing = existingData?.data.find((row) => row.stepId === stepId);

      const body: StepProgressUpsertBody = {
        clientRequestId: createIdempotencyKey(),
        phaseId: existing?.phaseId ?? null,
        status: data.status ?? existing?.status ?? "pending",
        completedAt:
          data.completedAt !== undefined
            ? data.completedAt
            : (existing?.completedAt ?? null),
        scheduledFor: existing?.scheduledFor ?? null,
        medications: data.medications ?? existing?.medications ?? [],
        data: data.data ?? existing?.data ?? {},
        notes:
          data.notes !== undefined ? data.notes : (existing?.notes ?? null),
      };

      const {
        data: saved,
        error,
        response,
      } = await client.PUT("/cycles/{cycleId}/step-progress/{stepId}", {
        params: { path: { cycleId, stepId } },
        body,
      });

      if (!saved) {
        throwApiError(
          error,
          "Failed to update step progress",
          "useUpdateStepProgress",
          response?.status,
        );
      }

      return { data: saved };
    },
    // Optimistic update: immediately update the UI before the mutation completes
    onMutate: async ({ cycleId, stepId, data }) => {
      // Build query key - must match the key used in useCycles
      const cyclesQueryKey = ["cycles", tenantId, patientId] as const;

      // Cancel any outgoing refetches to prevent overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: cyclesQueryKey });

      // Snapshot the previous value for rollback
      const previousCycles =
        queryClient.getQueryData<LocalCycle[]>(cyclesQueryKey);

      if (!previousCycles) {
        return { previousCycles: undefined, cyclesQueryKey };
      }

      // Build the updated cycles array
      const updatedCycles = previousCycles.map((cycle) => {
        if (cycle.id !== cycleId) return cycle;

        // Update the step progress for this cycle
        const existingProgress = cycle.stepProgress ?? [];
        const stepIndex = existingProgress.findIndex(
          (s) => s.stepId === stepId,
        );

        let newProgress: TreatmentStepProgress[];

        if (stepIndex === -1) {
          // Add new step progress with the update data merged in
          const newStep: TreatmentStepProgress = {
            stepId,
            status: (data.status as TreatmentStepStatus) ?? "pending",
            completedAt: data.completedAt ?? null,
            medications: data.medications ?? [],
            data: data.data ?? {},
            notes: data.notes ?? null,
          };
          newProgress = [...existingProgress, newStep];
        } else {
          // Update existing step progress
          newProgress = existingProgress.map((step, idx) =>
            idx === stepIndex
              ? {
                  ...step,
                  status: (data.status as TreatmentStepStatus) ?? step.status,
                  completedAt:
                    data.completedAt !== undefined
                      ? data.completedAt
                      : step.completedAt,
                  medications: data.medications ?? step.medications,
                  data: data.data ?? step.data,
                  notes: data.notes !== undefined ? data.notes : step.notes,
                }
              : step,
          );
        }

        // Determine if we need to advance the phase (optimistic update)
        let phaseUpdate: Partial<LocalCycle> = {};
        if (data.status === "completed" || data.status === "skipped") {
          const nextPhase = getNextPhaseForStep(cycle.cycleType, stepId);
          if (nextPhase) {
            const today = getTodayString();
            phaseUpdate = {
              currentPhase: nextPhase,
              phaseStartDate: today,
            };
          }
        }

        return {
          ...cycle,
          stepProgress: newProgress,
          ...phaseUpdate,
        };
      });

      // Use setQueryData with the exact key to trigger re-renders
      queryClient.setQueryData<LocalCycle[]>(cyclesQueryKey, updatedCycles);

      // Return context for potential rollback
      return { previousCycles, cyclesQueryKey };
    },
    // If the mutation fails, rollback to the previous value
    onError: (_error, _variables, context) => {
      if (context?.previousCycles && context?.cyclesQueryKey) {
        queryClient.setQueryData(
          context.cyclesQueryKey,
          context.previousCycles,
        );
      }
    },
    // After success or error, always refetch to confirm server state
    onSettled: () => {
      for (const key of cycleMutationInvalidationKeys()) {
        queryClient.invalidateQueries({ queryKey: key });
      }
    },
  });
}
